"use client";

import { useMemo } from "react";
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
} from "@tanstack/react-query";
import {
  hotelSearchAction,
  type HotelSearchPayload,
} from "@/actions/hotel.action";
import { formatDistance } from "@/utils/format/distance";
import type {
  ResultCardData,
  SortOption,
  TabOption,
} from "@/components/search-results/search.result.types";
import type { HotelSearchResponse } from "@/types/api";

export type HotelResultInput = {
  id?: string | number;
  uniqueId?: string;
  title?: string;
  price?: number | string | null;
  salePrice?: number | string | null;
  disPercent?: number | string | null;
  discount?: number | string | null;
  starRating?: number | string | null;
  rating?: number | string | null;
  totalBooking?: number | null;
  reviewCount?: number | null;
  mbcTotalReviews?: number | null;
  feature?: string | null;
  primary?: string | null;
  image?: string | null;
  distance?: number | string | null;
  distanceUnit?: string | null;
  distanceMeasurement?: string | null;
  hasBreakfast?: boolean | number | null;
  isInstantConfirm?: boolean | null;
  isFavorite?: boolean | null;
  favorite?: boolean | null;
  overallReview?: number | { overallRating?: number | string | null } | null;
  location?: {
    lat?: string | number | null;
    lng?: string | number | null;
    distance?: number | null;
    distanceUnit?: string | null;
    distanceMeasurement?: string | null;
  } | null;
  lat?: string | number;
  lng?: string | number;
  cancelPolicy?: {
    id?: number;
    message?: string | null;
  } | null;
  availMsg?: string | null;
  [key: string]: unknown;
};

export type UseHandleSearchHotelResultsParams = {
  locationId: string;
  search: string;
  lat?: number | null;
  lng?: number | null;
  start: string;
  end: string;
  tab?: TabOption;
  sortBy?: SortOption;
  adults: number;
  children: number;
  rooms: number;
  priceRange: number[];
  propertyType: string[];
  propertyRating: string[];
  numberOfBeds: string[];
  facilities: string[];
  distance: string[];
  enabled?: boolean;
};

const toCsv = (values: string[]) => values.filter(Boolean).join(",");

export const toHotelResultCardData = (
  item: HotelResultInput,
): ResultCardData => {
  const price = Number(item.price) || 0;
  const salePrice = Number(item.salePrice) || 0;
  const hasSalePrice = salePrice > 0 && salePrice < price;
  const disPercent = Number(item.disPercent) || 0;

  const rawLat = item.location?.lat ?? item.lat;
  const latParsed = Number.parseFloat(rawLat ? String(rawLat) : "");
  const lat = Number.isFinite(latParsed) ? latParsed : undefined;

  const rawLng = item.location?.lng ?? item.lng;
  const lngParsed = Number.parseFloat(rawLng ? String(rawLng) : "");
  const lng = Number.isFinite(lngParsed) ? lngParsed : undefined;

  const rating =
    typeof item.overallReview === "number"
      ? item.overallReview
      : item.overallReview &&
          typeof item.overallReview === "object" &&
          "overallRating" in item.overallReview
        ? Number(item.overallReview.overallRating)
        : Number(item.starRating) || 0;

  const reviewCount =
    item.totalBooking ?? item.reviewCount ?? item.mbcTotalReviews ?? 0;

  const rawDistance = item.location?.distance ?? item.distance;
  const distanceValue =
    typeof rawDistance === "number" && Number.isFinite(rawDistance)
      ? rawDistance
      : 0;

  const distanceUnit = item.location?.distanceUnit ?? item.distanceUnit ?? "km";
  const distanceMeasurement =
    item.location?.distanceMeasurement ??
    item.distanceMeasurement ??
    "city_center";

  return {
    ...item,
    id: String(item.id ?? ""),
    primary: item.primary || item.feature || item.image || null,
    image: item.primary || item.feature || item.image || "",
    title: item.title ?? "",
    lat,
    lng,
    rating,
    reviewCount,
    distance: formatDistance(distanceValue, distanceUnit, distanceMeasurement),
    distanceFormatted: formatDistance(
      distanceValue,
      distanceUnit,
      distanceMeasurement,
    ),
    price: hasSalePrice ? price : price,
    originalPrice: hasSalePrice ? price : undefined,
    salePrice: hasSalePrice ? salePrice : undefined,
    hasSalePrice,
    disPercent: disPercent > 0 ? disPercent : undefined,
    discount: disPercent > 0 ? disPercent : undefined,
    isFavorite: Boolean(item.isFavorite ?? item.favorite),
    isInstantConfirm: Boolean(item.isInstantConfirm),
    cancelPolicy: item.cancelPolicy ?? null,
    hasBreakfast: Boolean(item.hasBreakfast),
    availMsg: String(item.availMsg ?? ""),
    totalBooking: Number(item.totalBooking) || reviewCount,
    starRating: Number(item.starRating) || rating,
  };
};

/**
 * Mutation hook for manual hotel search execution via TanStack Query
 */
export const useSearchHotelMutation = () => {
  return useMutation<HotelSearchResponse, Error, HotelSearchPayload>({
    mutationFn: (payload) => hotelSearchAction(payload),
  });
};

/**
 * Infinite Query search hook for paginated hotel search results
 */
export const useHandleSearchHotelResults = ({
  locationId,
  search,
  lat,
  lng,
  start,
  end,
  tab,
  sortBy,
  adults,
  children,
  rooms,
  priceRange,
  propertyType,
  propertyRating,
  numberOfBeds,
  facilities,
  distance,
  enabled = true,
}: UseHandleSearchHotelResultsParams) => {
  const hasCoordinates = Number.isFinite(lat) && Number.isFinite(lng);
  const normalizedStart = start.trim();
  const normalizedEnd = end.trim() || normalizedStart;
  const normalizedLocationId = locationId.trim();
  const normalizedSearch = search.trim();
  const hasSearch = normalizedSearch.length > 0;
  const hasLocation = hasCoordinates || normalizedLocationId.length > 0;
  const canSearch =
    (hasLocation || hasSearch) &&
    normalizedStart.length > 0 &&
    normalizedEnd.length > 0;
  const isQueryEnabled = enabled && canSearch;

  const payload = useMemo<HotelSearchPayload>(
    () => ({
      adults,
      children,
      room: rooms,
      start: normalizedStart,
      end: normalizedEnd,
      type: tab ?? "popular",
      sortBy,
      search: normalizedSearch || undefined,
      lat: hasCoordinates ? String(lat) : undefined,
      lng: hasCoordinates ? String(lng) : undefined,
      locationId:
        hasSearch || hasCoordinates
          ? undefined
          : normalizedLocationId || undefined,
      bed: toCsv(numberOfBeds),
      star: toCsv(propertyRating),
      attributes: toCsv(Array.from(new Set([...propertyType, ...facilities]))),
      distance: toCsv(distance),
      limit: 10,
    }),
    [
      adults,
      children,
      rooms,
      normalizedStart,
      normalizedEnd,
      tab,
      sortBy,
      normalizedSearch,
      hasCoordinates,
      lat,
      lng,
      hasSearch,
      normalizedLocationId,
      numberOfBeds,
      propertyRating,
      propertyType,
      facilities,
      distance,
    ],
  );

  const query = useInfiniteQuery<HotelSearchResponse, Error>({
    queryKey: ["hotel-search-results", payload, priceRange],
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      hotelSearchAction({
        ...payload,
        cursor: typeof pageParam === "string" ? pageParam : undefined,
      }),
    getNextPageParam: (lastPage) => lastPage.data.nextCursor ?? undefined,
    enabled: isQueryEnabled,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });

  const results = useMemo(() => {
    const items = query.data?.pages.flatMap((page) => page.data.items) ?? [];
    return items.map(toHotelResultCardData);
  }, [query.data?.pages]);

  const nextCursor = query.data?.pages.at(-1)?.data.nextCursor ?? null;

  return {
    canSearch,
    isLoadingResults: isQueryEnabled && query.isPending,
    isLoadingMoreResults: query.isFetchingNextPage,
    isFetchingResults: query.isFetching && !query.isFetchingNextPage,
    results,
    total: query.data?.pages[0]?.data.total ?? results.length,
    nextCursor,
    hasMoreResults: Boolean(nextCursor),
    error: query.error,
    loadMoreResults: () => void query.fetchNextPage(),
  };
};
